import { useEffect, useState, type CSSProperties } from "react";
import {
  getActiveShift,
  addShiftEntry,
  getDailyChecklist,
  toggleDailyChecklistItem,
} from "../../lib/api";

interface LogEntry {
  id: string;
  type: "note" | "incident" | "equipment_issue" | "inventory_shortage" | "guest_complaint" | "guest_compliment";
  content: string;
  posted_at: string;
  posted_by: string;
}

interface ChecklistItem {
  id: string;
  task: string;
  category: "opening" | "closing";
  completed: boolean;
  completed_by?: string | null;
}

export default function AdminOperations() {
  const [activeTab, setActiveTab] = useState<"shiftlog" | "checklists" | "maintenance" | "recovery">("shiftlog");
  
  // Shift state
  const [activeShiftId, setActiveShiftId] = useState<string | null>(null);
  const [isShiftActive, setIsShiftActive] = useState(true);
  const [shiftStart, setShiftStart] = useState<string>(new Date(Date.now() - 4 * 3600000).toISOString());
  const [managerOnDuty, setManagerOnDuty] = useState("Staff");
  
  // Shift log entries
  const [logType, setLogType] = useState<LogEntry["type"]>("note");
  const [newEntryText, setNewEntryText] = useState("");
  const [entries, setEntries] = useState<LogEntry[]>(() => {
    try {
      const cached = localStorage.getItem("station.shift_entries_cache");
      if (cached) return JSON.parse(cached);
    } catch {}
    return [];
  });

  // Checklists
  const [checklists, setChecklists] = useState<ChecklistItem[]>(() => {
    try {
      const cached = localStorage.getItem("station.checklists_cache");
      if (cached) return JSON.parse(cached);
    } catch {}
    return [];
  });

  // Custom task form
  const [newCustomTask, setNewCustomTask] = useState("");
  const [customCategory, setCustomCategory] = useState<"opening" | "closing">("opening");
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [showPrintDebrief, setShowPrintDebrief] = useState(false);

  // Maintenance issues
  const [maintenance] = useState<{ id: string; item: string; priority: "P0" | "P1" | "P2"; status: "pending" | "fixed"; reported_at: string }[]>([
    { id: "m1", item: "Draft Line #4 pressure regulator leaking slowly", priority: "P1", status: "pending", reported_at: new Date(Date.now() - 24 * 3600000).toISOString() },
    { id: "m2", item: "Under-counter refrigeration gasket cracked", priority: "P2", status: "fixed", reported_at: new Date(Date.now() - 72 * 3600000).toISOString() }
  ]);

  // Service Recovery
  const [recovery] = useState<{ id: string; guest: string; issue: string; compensation: string; resolved: boolean }[]>([
    { id: "r1", guest: "Jonathan S.", issue: "Slightly delayed service during private tasting rush", compensation: "Complimentary pour of Weller Antique 107", resolved: true }
  ]);

  const [loading, setLoading] = useState(() => {
    try {
      return !localStorage.getItem("station.checklists_cache");
    } catch {
      return true;
    }
  });

  const loadOperations = async () => {
    try {
      const [shiftRes, chkRes] = await Promise.allSettled([
        getActiveShift(),
        getDailyChecklist(),
      ]);

      if (shiftRes.status === "fulfilled" && shiftRes.value?.ok && shiftRes.value.shift) {
        setActiveShiftId(shiftRes.value.shift.id);
        setIsShiftActive(shiftRes.value.shift.status === "open");
        if (shiftRes.value.shift.opened_at) setShiftStart(shiftRes.value.shift.opened_at);
        if (shiftRes.value.shift.customers?.display_name) {
          setManagerOnDuty(shiftRes.value.shift.customers.display_name);
        }
        if (Array.isArray(shiftRes.value.entries) && shiftRes.value.entries.length > 0) {
          const mappedEntries = shiftRes.value.entries.map((e: any) => ({
            id: e.id,
            type: e.entry_type || "note",
            content: e.content,
            posted_at: e.created_at,
            posted_by: e.logged_by_name || "Staff",
          }));
          setEntries(mappedEntries);
          try {
            localStorage.setItem("station.shift_entries_cache", JSON.stringify(mappedEntries));
          } catch {}
        }
      }

      if (chkRes.status === "fulfilled" && chkRes.value?.ok && Array.isArray(chkRes.value.items) && chkRes.value.items.length > 0) {
        const mappedChecklist = chkRes.value.items.map((c: any) => ({
          id: c.id,
          task: c.title,
          category: c.category || "opening",
          completed: !!c.completed,
          completed_by: c.completed_by || null,
        }));
        setChecklists(mappedChecklist);
        try {
          localStorage.setItem("station.checklists_cache", JSON.stringify(mappedChecklist));
        } catch {}
      } else if (checklists.length === 0) {
        const defaults: ChecklistItem[] = [
          { id: "c1", task: "Verify ice machine level and start cooling wells", category: "opening", completed: true, completed_by: "Lead" },
          { id: "c2", task: "Sanitize and inspect all glassware for chips", category: "opening", completed: true, completed_by: "Bar" },
          { id: "c3", task: "Count cash drawer and verify opening standard float", category: "opening", completed: true, completed_by: "Lead" },
          { id: "c4", task: "Pre-batch fresh cocktail citrus and store in chillers", category: "opening", completed: false },
          { id: "c5", task: "Wipe down back bar shelves and dust rare bottle library", category: "closing", completed: false },
          { id: "c6", task: "Flush draft line drip trays with sanitizing solution", category: "closing", completed: false },
          { id: "c7", task: "Lock rare liquor cabinet and deposit safe bags in drop safe", category: "closing", completed: false }
        ];
        setChecklists(defaults);
        try {
          localStorage.setItem("station.checklists_cache", JSON.stringify(defaults));
        } catch {}
      }
    } catch (err) {
      console.warn("Failed to load operations data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOperations();
  }, []);

  async function handlePostEntry() {
    if (!newEntryText.trim()) return;
    const tempEntry: LogEntry = {
      id: `e_${Date.now()}`,
      type: logType,
      content: newEntryText,
      posted_at: new Date().toISOString(),
      posted_by: managerOnDuty,
    };
    const updated = [tempEntry, ...entries];
    setEntries(updated);
    setNewEntryText("");
    try {
      localStorage.setItem("station.shift_entries_cache", JSON.stringify(updated));
    } catch {}

    try {
      await addShiftEntry({
        shift_id: activeShiftId || undefined,
        entry_type: logType,
        content: tempEntry.content,
      });
    } catch (err) {
      console.warn("Failed to persist shift entry to backend:", err);
    }
  }

  async function toggleChecklist(id: string) {
    const item = checklists.find((c) => c.id === id);
    if (!item) return;
    const prevCompleted = item.completed;
    const nextCompleted = !prevCompleted;
    const updated = checklists.map((c) =>
      c.id === id
        ? { ...c, completed: nextCompleted, completed_by: nextCompleted ? managerOnDuty.split(" ")[0] : null }
        : c
    );
    setChecklists(updated);
    try {
      localStorage.setItem("station.checklists_cache", JSON.stringify(updated));
    } catch {}

    if (!id.startsWith("custom_")) {
      try {
        await toggleDailyChecklistItem({
          item_id: id,
          completed: nextCompleted,
        });
      } catch (err) {
        console.warn("Failed to persist checklist toggle to server:", err);
        // If network rejects write, alert manager
        alert("⚠️ Checklist update failed to sync with the server. Please verify your connection.");
      }
    }
  }

  function handleAddCustomTask() {
    if (!newCustomTask.trim()) return;
    const newTask: ChecklistItem = {
      id: `custom_${Date.now()}`,
      task: `${newCustomTask.trim()} [Station Note]`,
      category: customCategory,
      completed: false,
    };
    const updated = [...checklists, newTask];
    setChecklists(updated);
    setNewCustomTask("");
    setShowAddCustom(false);
    try {
      localStorage.setItem("station.checklists_cache", JSON.stringify(updated));
    } catch {}
  }

  function handleEndShift() {
    setIsShiftActive(false);
  }

  function handleStartShift() {
    setIsShiftActive(true);
    setShiftStart(new Date().toISOString());
  }

  function fmtTime(iso: string) {
    return new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }

  if (loading) {
    return <div style={styles.loader}>Loading Operations Dashboard…</div>;
  }

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <h1 style={styles.title}>Shift Operations Hub</h1>
        <p style={styles.subtitle}>Log shift events, manage opening/closing checklists, track maintenance, and handle guest recovery.</p>
      </header>

      {/* Tabs */}
      <div style={styles.tabsRow}>
        <button onClick={() => setActiveTab("shiftlog")} style={{ ...styles.tab, borderBottomColor: activeTab === "shiftlog" ? "var(--gold)" : "transparent", color: activeTab === "shiftlog" ? "var(--gold)" : "var(--muted)" }}>Shift Log</button>
        <button onClick={() => setActiveTab("checklists")} style={{ ...styles.tab, borderBottomColor: activeTab === "checklists" ? "var(--gold)" : "transparent", color: activeTab === "checklists" ? "var(--gold)" : "var(--muted)" }}>Checklists</button>
        <button onClick={() => setActiveTab("maintenance")} style={{ ...styles.tab, borderBottomColor: activeTab === "maintenance" ? "var(--gold)" : "transparent", color: activeTab === "maintenance" ? "var(--gold)" : "var(--muted)" }}>Maintenance</button>
        <button onClick={() => setActiveTab("recovery")} style={{ ...styles.tab, borderBottomColor: activeTab === "recovery" ? "var(--gold)" : "transparent", color: activeTab === "recovery" ? "var(--gold)" : "var(--muted)" }}>Service Recovery</button>
      </div>

      {activeTab === "shiftlog" && (
        <div style={styles.tabContent}>
          {/* Active Shift Card */}
          <div style={styles.card}>
            <div style={styles.cardHeader}>
              <h2 style={styles.cardTitle}>Current Shift Status</h2>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  type="button"
                  onClick={() => setShowPrintDebrief(true)}
                  style={{
                    background: "rgba(249,175,54,0.1)",
                    border: "1px solid var(--gold, #F9AF36)",
                    color: "var(--gold, #F9AF36)",
                    borderRadius: 8,
                    padding: "4px 10px",
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  📄 View Debrief
                </button>
                <span style={{ ...styles.badge, background: isShiftActive ? "rgba(39, 174, 96, 0.15)" : "rgba(163, 157, 152, 0.15)", color: isShiftActive ? "#86efac" : "var(--muted)" }}>
                  {isShiftActive ? "ACTIVE" : "ENDED"}
                </span>
              </div>
            </div>
            {isShiftActive ? (
              <div style={styles.shiftDetails}>
                <p style={styles.detailText}><strong>Manager on Duty:</strong> {managerOnDuty}</p>
                <p style={styles.detailText}><strong>Shift Started:</strong> {fmtTime(shiftStart)}</p>
                <div style={{ display: "flex", gap: 10, marginTop: 12 }}>
                  <button onClick={handleEndShift} style={styles.dangerBtn}>End Shift & Generate Debrief</button>
                  <button onClick={() => window.print()} style={{ ...styles.pillBtn, borderColor: "var(--border)" }}>🖨️ Print Nightly Report</button>
                </div>
              </div>
            ) : (
              <div style={styles.shiftDetails}>
                <p style={styles.detailText}>No active shift is currently running.</p>
                <button onClick={handleStartShift} style={styles.successBtn}>Start Fresh Shift</button>
              </div>
            )}
          </div>

          {/* Form to submit log entries */}
          {isShiftActive && (
            <div style={{ ...styles.card, marginTop: 20 }}>
              <h3 style={styles.subCardTitle}>Add Entry to Today's Shift Log</h3>
              <div style={styles.pillGroup}>
                {(["note", "incident", "equipment_issue", "inventory_shortage", "guest_compliment"] as const).map((type) => (
                  <button key={type} onClick={() => setLogType(type)} style={{ ...styles.pillBtn, background: logType === type ? "var(--gold)" : "var(--bg)", color: logType === type ? "#0D0B08" : "var(--text)" }}>
                    {type.replace("_", " ")}
                  </button>
                ))}
              </div>
              <textarea value={newEntryText} onChange={(e) => setNewEntryText(e.target.value)} placeholder="Type shift note or log entry details here..." style={styles.textarea} />
              <button onClick={handlePostEntry} style={styles.saveBtn}>Post Entry to Shift Log</button>
            </div>
          )}

          {/* Today's Log Feed */}
          <div style={{ marginTop: 24 }}>
            <h3 style={styles.feedTitle}>Today's Log Entries</h3>
            <div style={styles.feedList}>
              {entries.map((entry) => (
                <div key={entry.id} style={styles.feedItem}>
                  <div style={styles.feedMeta}>
                    <span style={styles.feedPoster}>{entry.posted_by}</span>
                    <span style={styles.feedTime}>{fmtTime(entry.posted_at)}</span>
                    <span style={{ ...styles.feedType, borderColor: entry.type === "incident" ? "var(--red)" : "var(--border)" }}>{entry.type.replace("_", " ")}</span>
                  </div>
                  <p style={styles.feedText}>{entry.content}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "checklists" && (
        <div style={styles.tabContent}>
          {/* Header & Add Custom Task Button */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: "var(--gold, #F9AF36)" }}>Daily Operating Checklists</h2>
              <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--muted)" }}>
                Opening & closing verification for bar wells, glassware, cash float, and lockup.
              </p>
            </div>
            <button
              type="button"
              onClick={() => setShowAddCustom(!showAddCustom)}
              style={{
                background: "var(--gold, #F9AF36)",
                color: "#000",
                border: "none",
                borderRadius: 8,
                padding: "8px 14px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              {showAddCustom ? "✕ Cancel" : "+ Add Shift Task"}
            </button>
          </div>

          {/* Add custom task inline drawer */}
          {showAddCustom && (
            <div style={{ background: "var(--surface, #18181b)", border: "1px solid var(--border, #27272a)", borderRadius: 12, padding: 16, marginBottom: 16 }}>
              <h3 style={{ margin: "0 0 8px", fontSize: 13, fontWeight: 700, color: "var(--text)" }}>Add Custom Shift Task</h3>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <input
                  type="text"
                  placeholder="Task title (e.g. Prep 30 VIP punch bowls for 8 PM)"
                  value={newCustomTask}
                  onChange={(e) => setNewCustomTask(e.target.value)}
                  style={{ flex: 1, minWidth: 240, background: "var(--bg, #09090b)", border: "1px solid var(--border, #27272a)", borderRadius: 8, padding: "8px 12px", color: "var(--text)", fontSize: 13 }}
                />
                <select
                  value={customCategory}
                  onChange={(e) => setCustomCategory(e.target.value as any)}
                  style={{ background: "var(--bg, #09090b)", border: "1px solid var(--border, #27272a)", borderRadius: 8, padding: "8px 12px", color: "var(--text)", fontSize: 13 }}
                >
                  <option value="opening">Opening Shift</option>
                  <option value="closing">Closing Shift</option>
                </select>
                <button
                  type="button"
                  onClick={handleAddCustomTask}
                  disabled={!newCustomTask.trim()}
                  style={{ background: "var(--gold, #F9AF36)", color: "#000", border: "none", borderRadius: 8, padding: "8px 18px", fontWeight: 700, cursor: "pointer" }}
                >
                  Save Task
                </button>
              </div>
            </div>
          )}

          <div style={styles.layoutGrid}>
            {/* Opening Checklist */}
            <div style={styles.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h2 style={styles.cardTitle}>Opening Shift Tasks</h2>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--gold, #F9AF36)" }}>
                  {checklists.filter(c => c.category === "opening" && c.completed).length} / {checklists.filter(c => c.category === "opening").length} Done
                </span>
              </div>
              <div style={styles.checklistList}>
                {checklists.filter(c => c.category === "opening").map(item => (
                  <label key={item.id} style={styles.checkRow}>
                    <input type="checkbox" checked={item.completed} onChange={() => toggleChecklist(item.id)} style={styles.checkbox} />
                    <span style={{ ...styles.checkText, textDecoration: item.completed ? "line-through" : "none", color: item.completed ? "var(--muted)" : "var(--text)" }}>
                      {item.task} {item.completed_by && <span style={styles.tag}>✓ {item.completed_by}</span>}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Closing Checklist */}
            <div style={styles.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h2 style={styles.cardTitle}>Closing Shift Tasks</h2>
                <span style={{ fontSize: 12, fontWeight: 700, color: "var(--gold, #F9AF36)" }}>
                  {checklists.filter(c => c.category === "closing" && c.completed).length} / {checklists.filter(c => c.category === "closing").length} Done
                </span>
              </div>
              <div style={styles.checklistList}>
                {checklists.filter(c => c.category === "closing").map(item => (
                  <label key={item.id} style={styles.checkRow}>
                    <input type="checkbox" checked={item.completed} onChange={() => toggleChecklist(item.id)} style={styles.checkbox} />
                    <span style={{ ...styles.checkText, textDecoration: item.completed ? "line-through" : "none", color: item.completed ? "var(--muted)" : "var(--text)" }}>
                      {item.task} {item.completed_by && <span style={styles.tag}>✓ {item.completed_by}</span>}
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === "maintenance" && (
        <div style={styles.tabContent}>
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Reported Hardware & Maintenance Tickets</h2>
            <div style={styles.checklistList}>
              {maintenance.map(m => (
                <div key={m.id} style={styles.issueRow}>
                  <div style={styles.issueMeta}>
                    <span style={{ ...styles.priorityTag, background: m.priority === "P0" ? "rgba(224, 80, 64, 0.15)" : "rgba(249, 175, 54, 0.15)", color: m.priority === "P0" ? "var(--red)" : "var(--gold)" }}>
                      {m.priority}
                    </span>
                    <span style={{ ...styles.statusTag, background: m.status === "fixed" ? "rgba(39, 174, 96, 0.15)" : "rgba(249, 175, 54, 0.15)", color: m.status === "fixed" ? "#86efac" : "var(--gold)" }}>
                      {(m.status || "unknown").toUpperCase()}
                    </span>
                  </div>
                  <div style={styles.issueText}>{m.item}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === "recovery" && (
        <div style={styles.tabContent}>
          <div style={styles.card}>
            <h2 style={styles.cardTitle}>Active Service Recovery Log</h2>
            <div style={styles.checklistList}>
              {recovery.map(rec => (
                <div key={rec.id} style={styles.issueRow}>
                  <div style={styles.issueMeta}>
                    <span style={styles.guestName}>{rec.guest}</span>
                    <span style={{ ...styles.statusTag, background: "rgba(39, 174, 96, 0.15)", color: "#86efac" }}>COMPLETED</span>
                  </div>
                  <div style={styles.issueText}>
                    <p style={{ margin: "4px 0" }}><strong>Issue:</strong> {rec.issue}</p>
                    <p style={{ margin: "4px 0", color: "var(--gold)" }}><strong>Compensation:</strong> {rec.compensation}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Shift Debrief / Print Preview Modal */}
      {showPrintDebrief && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }} onClick={() => setShowPrintDebrief(false)}>
          <div style={{ background: "#18181b", border: "1px solid #27272a", borderRadius: 16, width: "100%", maxWidth: 640, maxHeight: "90vh", overflowY: "auto", padding: 24, boxShadow: "0 10px 40px rgba(0,0,0,0.9)" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #27272a", paddingBottom: 12, marginBottom: 16 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, color: "var(--gold, #F9AF36)", fontWeight: 800 }}>Daily Shift Debrief & Handover</h2>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: "var(--muted)" }}>The Sentinel Room · {new Date().toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric", year: "numeric" })}</p>
              </div>
              <button type="button" onClick={() => setShowPrintDebrief(false)} style={{ background: "transparent", border: "none", color: "#a1a1aa", fontSize: 20, cursor: "pointer" }}>✕</button>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, background: "#09090b", padding: 12, borderRadius: 8, marginBottom: 16 }}>
              <div><strong style={{ color: "#a1a1aa", fontSize: 11, textTransform: "uppercase" }}>Manager on Duty:</strong> <div style={{ fontSize: 14, fontWeight: 700 }}>{managerOnDuty}</div></div>
              <div><strong style={{ color: "#a1a1aa", fontSize: 11, textTransform: "uppercase" }}>Shift Start:</strong> <div style={{ fontSize: 14, fontWeight: 700 }}>{fmtTime(shiftStart)}</div></div>
            </div>

            {/* Checklist Completion Summary */}
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 13, textTransform: "uppercase", color: "var(--gold)", margin: "0 0 8px" }}>Checklist Execution</h3>
              <div style={{ display: "flex", gap: 16 }}>
                <div style={{ flex: 1, background: "#09090b", padding: 10, borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#86efac" }}>{checklists.filter(c => c.category === "opening" && c.completed).length} / {checklists.filter(c => c.category === "opening").length}</div>
                  <div style={{ fontSize: 11, color: "#a1a1aa" }}>Opening Tasks Completed</div>
                </div>
                <div style={{ flex: 1, background: "#09090b", padding: 10, borderRadius: 8, textAlign: "center" }}>
                  <div style={{ fontSize: 18, fontWeight: 800, color: "#86efac" }}>{checklists.filter(c => c.category === "closing" && c.completed).length} / {checklists.filter(c => c.category === "closing").length}</div>
                  <div style={{ fontSize: 11, color: "#a1a1aa" }}>Closing Tasks Completed</div>
                </div>
              </div>
            </div>

            {/* Shift Log Entries */}
            <div style={{ marginBottom: 16 }}>
              <h3 style={{ fontSize: 13, textTransform: "uppercase", color: "var(--gold)", margin: "0 0 8px" }}>Shift Log & Incidents ({entries.length})</h3>
              {entries.length === 0 ? (
                <p style={{ fontSize: 12, color: "#a1a1aa", fontStyle: "italic", margin: 0 }}>No entries logged for this shift.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {entries.map(e => (
                    <div key={e.id} style={{ background: "#09090b", padding: "8px 12px", borderRadius: 6, fontSize: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", color: "#a1a1aa", marginBottom: 2 }}>
                        <span><strong>{e.posted_by}</strong> · <span style={{ textTransform: "uppercase", color: e.type === "incident" ? "#ef4444" : "#a1a1aa" }}>{e.type.replace("_", " ")}</span></span>
                        <span>{fmtTime(e.posted_at)}</span>
                      </div>
                      <div style={{ color: "#f4f4f5" }}>{e.content}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Service Recovery */}
            {recovery.length > 0 && (
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 13, textTransform: "uppercase", color: "var(--gold)", margin: "0 0 8px" }}>Guest Service Recovery</h3>
                {recovery.map(r => (
                  <div key={r.id} style={{ background: "#09090b", padding: "8px 12px", borderRadius: 6, fontSize: 12 }}>
                    <div><strong>{r.guest}</strong>: {r.issue}</div>
                    <div style={{ color: "var(--gold)", marginTop: 2 }}>Comp: {r.compensation}</div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button type="button" onClick={() => setShowPrintDebrief(false)} style={{ background: "#27272a", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontWeight: 700 }}>Close</button>
              <button type="button" onClick={() => window.print()} style={{ background: "var(--gold, #F9AF36)", color: "#000", border: "none", borderRadius: 8, padding: "8px 18px", cursor: "pointer", fontWeight: 700 }}>🖨️ Print Shift Debrief</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    padding: "24px 16px",
    background: "var(--bg, #0C0907)",
    color: "var(--text, #F7F5F0)",
    minHeight: "100vh",
  },
  header: { marginBottom: 24 },
  title: {
    fontFamily: "Georgia, serif",
    fontSize: 28,
    fontWeight: 400,
    margin: "0 0 4px",
    color: "var(--text, #F7F5F0)",
  },
  subtitle: {
    color: "var(--muted, #A39D98)",
    fontSize: 14,
    margin: 0,
  },
  tabsRow: {
    display: "flex",
    gap: 16,
    borderBottom: "1px solid var(--border, #231D19)",
    marginBottom: 24,
    overflowX: "auto",
  },
  tab: {
    padding: "12px 16px",
    fontSize: 13,
    fontWeight: "bold",
    cursor: "pointer",
    background: "none",
    border: "none",
    outline: "none",
    borderBottom: "3px solid transparent",
    transition: "all 0.2s ease",
    whiteSpace: "nowrap",
  },
  tabContent: {
    width: "100%",
  },
  card: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 16,
    padding: 24,
    maxWidth: 800,
  },
  cardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  cardTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 18,
    fontWeight: 400,
    margin: 0,
  },
  subCardTitle: {
    fontSize: 14,
    fontWeight: "bold",
    margin: "0 0 12px",
    color: "var(--muted)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  badge: {
    fontSize: 10,
    fontWeight: 800,
    padding: "4px 10px",
    borderRadius: 8,
    letterSpacing: "0.5px",
  },
  shiftDetails: {
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  detailText: {
    fontSize: 14,
    margin: 0,
  },
  dangerBtn: {
    alignSelf: "flex-start",
    background: "var(--red, #E05040)",
    color: "#FFFFFF",
    border: 0,
    borderRadius: 8,
    padding: "10px 16px",
    fontSize: 12,
    fontWeight: "bold",
    cursor: "pointer",
  },
  successBtn: {
    alignSelf: "flex-start",
    background: "#27ae60",
    color: "#FFFFFF",
    border: 0,
    borderRadius: 8,
    padding: "10px 16px",
    fontSize: 12,
    fontWeight: "bold",
    cursor: "pointer",
  },
  pillGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginBottom: 16,
  },
  pillBtn: {
    border: "1px solid var(--border, #231D19)",
    borderRadius: 20,
    padding: "6px 14px",
    fontSize: 11,
    fontWeight: "bold",
    cursor: "pointer",
    outline: "none",
  },
  textarea: {
    width: "100%",
    background: "var(--bg, #0C0907)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 8,
    padding: "12px",
    color: "var(--text, #F7F5F0)",
    fontSize: 13,
    lineHeight: 1.5,
    resize: "vertical",
    minHeight: 80,
    marginBottom: 16,
    boxSizing: "border-box",
  },
  saveBtn: {
    background: "var(--gold, #F9AF36)",
    color: "#0D0B08",
    border: 0,
    borderRadius: 8,
    padding: "10px 20px",
    fontSize: 12,
    fontWeight: "bold",
    cursor: "pointer",
  },
  feedTitle: {
    fontFamily: "Georgia, serif",
    fontSize: 16,
    fontWeight: 400,
    margin: "0 0 16px",
  },
  feedList: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
    maxWidth: 800,
  },
  feedItem: {
    background: "var(--surface, #15110E)",
    border: "1px solid var(--border, #231D19)",
    borderRadius: 12,
    padding: 16,
  },
  feedMeta: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginBottom: 8,
  },
  feedPoster: {
    fontSize: 13,
    fontWeight: "bold",
  },
  feedTime: {
    fontSize: 12,
    color: "var(--muted)",
  },
  feedType: {
    fontSize: 10,
    fontWeight: "bold",
    textTransform: "uppercase",
    padding: "2px 8px",
    borderRadius: 6,
    border: "1px solid",
    color: "var(--muted)",
    letterSpacing: "0.5px",
  },
  feedText: {
    fontSize: 14,
    margin: 0,
    lineHeight: 1.5,
  },
  layoutGrid: {
    display: "grid",
    // min(350px, 100%): one full-width column when the box is narrower than 350px (phones);
    // unchanged wherever 350px fits.
    gridTemplateColumns: "repeat(auto-fit, minmax(min(350px, 100%), 1fr))",
    gap: 24,
    maxWidth: 1000,
  },
  checklistList: {
    display: "flex",
    flexDirection: "column",
    gap: 14,
  },
  checkRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    cursor: "pointer",
  },
  checkbox: {
    marginTop: 3,
    flexShrink: 0, // a long task label squeezed the box to a dot
  },
  checkText: {
    fontSize: 13,
    lineHeight: 1.4,
  },
  tag: {
    background: "rgba(249, 175, 54, 0.12)",
    color: "var(--gold)",
    fontSize: 10,
    fontWeight: "bold",
    padding: "2px 6px",
    borderRadius: 6,
    marginLeft: 6,
  },
  issueRow: {
    borderBottom: "1px solid var(--border, #231D19)",
    paddingBottom: 14,
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  issueMeta: {
    display: "flex",
    gap: 8,
  },
  priorityTag: {
    fontSize: 9,
    fontWeight: 800,
    padding: "2px 6px",
    borderRadius: 4,
  },
  statusTag: {
    fontSize: 9,
    fontWeight: 800,
    padding: "2px 6px",
    borderRadius: 4,
  },
  issueText: {
    fontSize: 13,
    lineHeight: 1.4,
  },
  guestName: {
    fontSize: 13,
    fontWeight: "bold",
  },
  loader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "100vh",
    color: "var(--muted, #A39D98)",
    fontSize: 14,
  },
};
